// payment details page
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Avatar,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Close as CloseIcon,
  Payment as PaymentIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
  Receipt as ReceiptIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  Schedule as ScheduleIcon,
  Email as EmailIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  ReceiptLong as InvoiceIcon,
  Security as SecurityIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';

const PaymentDetails = ({ open, onClose, payment }) => {
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  if (!payment) return null;

  const handleResendReceipt = () => {
    setSnackbar({
      open: true,
      message: 'Receipt sent successfully to customer email',
      severity: 'success'
    });
  };

  const handleRefundPayment = () => {
    setSnackbar({
      open: true,
      message: 'Refund request submitted successfully',
      severity: 'success'
    });
  };

  const handleDownloadReceipt = () => {
    setSnackbar({
      open: true,
      message: 'Receipt downloaded successfully',
      severity: 'success'
    });
  };

  const handlePrintReceipt = () => {
    setSnackbar({
      open: true,
      message: 'Printing receipt...',
      severity: 'info'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: 'success',
      pending: 'warning',
      failed: 'error',
      refunded: 'info',
      cancelled: 'default'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <SuccessIcon />;
      case 'pending':
        return <PendingIcon />;
      case 'failed':
        return <ErrorIcon />;
      default:
        return <ScheduleIcon />;
    }
  };

  const getMethodIcon = (method) => {
    switch (method) {
      case 'credit_card':
        return <CreditCardIcon />;
      case 'bank_transfer':
        return <BankIcon />;
      case 'paypal':
        return <PaymentIcon />;
      default:
        return <PaymentIcon />;
    }
  };

  const getMethodLabel = (method) => {
    const labels = {
      credit_card: 'Credit Card',
      bank_transfer: 'Bank Transfer',
      paypal: 'PayPal',
      check: 'Check',
      cash: 'Cash'
    };
    return labels[method] || method;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: payment.currency || 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Mock transaction history
  const transactionHistory = [
    {
      id: 1,
      action: 'Payment Initiated',
      timestamp: payment.paymentDate,
      status: 'completed',
      description: 'Payment request submitted'
    },
    {
      id: 2,
      action: 'Payment Processed',
      timestamp: payment.processedAt || payment.paymentDate,
      status: payment.status,
      description: payment.gatewayResponse === 'succeeded' ? 'Payment processed successfully' : 'Payment processing failed'
    },
    {
      id: 3,
      action: 'Receipt Generated',
      timestamp: payment.processedAt || payment.paymentDate,
      status: 'completed',
      description: 'Payment receipt generated and sent'
    }
  ];

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0',
          pb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PaymentIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Payment Details
            </Typography>
            <Chip
              icon={getStatusIcon(payment.status)}
              label={payment.status.toUpperCase()}
              color={getStatusColor(payment.status)}
              size="small"
            />
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            {/* Payment Overview */}
            <Grid item xs={12} md={8}>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MoneyIcon color="primary" />
                    Payment Overview
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">Amount</Typography>
                      <Typography variant="h5" fontWeight="bold" color="primary">
                        {formatCurrency(payment.amount)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">Net Amount</Typography>
                      <Typography variant="h6" fontWeight="medium">
                        {formatCurrency(payment.netAmount)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">Processing Fee</Typography>
                      <Typography variant="body1">
                        {formatCurrency(payment.fee)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">Currency</Typography>
                      <Typography variant="body1">
                        {payment.currency}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Customer Information */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon color="primary" />
                    Customer Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">Customer Name</Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {payment.customerName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">Email</Typography>
                      <Typography variant="body1">
                        {payment.customerEmail}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">Invoice Number</Typography>
                      <Typography variant="body1" fontWeight="medium" fontFamily="monospace">
                        {payment.invoiceNumber}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">Description</Typography>
                      <Typography variant="body1">
                        {payment.description}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Payment Method Details */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CreditCardIcon color="primary" />
                    Payment Method
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getMethodIcon(payment.paymentMethod)}
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {getMethodLabel(payment.paymentMethod)}
                          </Typography>
                          {payment.cardLast4 && (
                            <Typography variant="body2" color="textSecondary">
                              **** {payment.cardLast4}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">Payment Gateway</Typography>
                      <Typography variant="body1">
                        {payment.gateway}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">Gateway Response</Typography>
                      <Typography variant="body1" fontFamily="monospace">
                        {payment.gatewayResponse}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Transaction Details & Actions */}
            <Grid item xs={12} md={4}>
              {/* Transaction Information */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SecurityIcon color="primary" />
                    Transaction Details
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="Transaction ID"
                        secondary={
                          <Typography variant="body2" fontFamily="monospace">
                            {payment.transactionId}
                          </Typography>
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Payment Date"
                        secondary={formatDate(payment.paymentDate)}
                      />
                    </ListItem>
                    {payment.processedAt && (
                      <ListItem>
                        <ListItemText
                          primary="Processed At"
                          secondary={formatDate(payment.processedAt)}
                        />
                      </ListItem>
                    )}
                  </List>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TimelineIcon color="primary" />
                    Quick Actions
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<EmailIcon />}
                      onClick={handleResendReceipt}
                      fullWidth
                    >
                      Resend Receipt
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownloadReceipt}
                      fullWidth
                    >
                      Download Receipt
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<PrintIcon />}
                      onClick={handlePrintReceipt}
                      fullWidth
                    >
                      Print Receipt
                    </Button>
                    {payment.status === 'completed' && (
                      <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<ReceiptIcon />}
                        onClick={handleRefundPayment}
                        fullWidth
                      >
                        Process Refund
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>

              {/* Transaction History */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TimelineIcon color="primary" />
                    Transaction History
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Action</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Time</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {transactionHistory.map((transaction) => (
                          <TableRow key={transaction.id}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {transaction.action}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {transaction.description}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={transaction.status}
                                color={getStatusColor(transaction.status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {formatDate(transaction.timestamp)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ 
          borderTop: '1px solid #e0e0e0', 
          pt: 2, 
          px: 3, 
          pb: 3,
          justifyContent: 'space-between'
        }}>
          <Box>
            <Typography variant="caption" color="textSecondary">
              Payment ID: {payment.id} | Created: {formatDate(payment.paymentDate)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose} variant="outlined">
              Close
            </Button>
            {payment.status === 'completed' && (
              <Button
                onClick={handleRefundPayment}
                variant="contained"
                color="warning"
                startIcon={<ReceiptIcon />}
              >
                Refund Payment
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PaymentDetails;